import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import ModulesPage from "@/pages/ModulesPage";
import BooksPage from "@/pages/BooksPage";
import B2BPage from "@/pages/B2BPage";
import ManifestoPage from "@/pages/ManifestoPage";
import ShopPage from "@/pages/ShopPage";
import AboutPage from "@/pages/AboutPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import PreviewNudge from "@/components/PreviewNudge";
import PeerPlusPage from "@/pages/PeerPlusPage";
import ModuleWatchPage from "@/pages/ModuleWatchPage";
import CinematicModulePage from "@/pages/CinematicModulePage";
import MyWorkbookPage from "@/pages/MyWorkbookPage";
import DashboardPage from "@/pages/DashboardPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminReport from "@/pages/AdminReport";
import IndustryPage from "@/pages/IndustryPage";
import LegalPage from "@/pages/LegalPage";
import PressKitPage from "@/pages/PressKitPage";
import EPICPilotPage from "@/pages/EPIC_PilotPage";
import AuthCallback from "@/pages/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GrainOverlay from "@/components/GrainOverlay";

import CoachChat from "@/components/CoachChat";

const BARE_ROUTES = ["/login", "/register", "/forgot-password", "/reset-password", "/verify-email"];

function Shell() {
  const location = useLocation();
  const bare = BARE_ROUTES.includes(location.pathname);

  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  // CRITICAL — Emergent Google OAuth returns via URL fragment `#session_id=...`.
  // Detect synchronously during render (NOT in useEffect) to avoid race conditions.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      {!bare && <Navigation />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/modules" element={<ModulesPage />} />
        <Route path="/books" element={<BooksPage />} />
        <Route path="/b2b" element={<B2BPage />} />
        <Route path="/for/:slug" element={<IndustryPage />} />
        <Route path="/terms" element={<LegalPage />} />
        <Route path="/privacy" element={<LegalPage />} />
        <Route path="/manifesto" element={<ManifestoPage />} />
        <Route path="/press" element={<PressKitPage />} />
        <Route path="/media-kit" element={<PressKitPage />} />
        <Route path="/pilot" element={<EPICPilotPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/what-we-do" element={<AboutPage />} />
        <Route path="/peer-plus" element={<PeerPlusPage />} />
        <Route path="/tacc-love" element={<PeerPlusPage />} />
        <Route path="/modules/:id/watch" element={<ModuleWatchPage />} />
        <Route path="/modules/:id/experience" element={<CinematicModulePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/workbook" element={<ProtectedRoute><MyWorkbookPage /></ProtectedRoute>} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/admin" element={<ProtectedRoute><AdminReport /></ProtectedRoute>} />
      </Routes>
      {!bare && <Footer />}
      {!bare && <CoachChat />}
      {!bare && <PreviewNudge />}
    </>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.4,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1C1917] antialiased selection:bg-[#B45309] selection:text-white">
      <GrainOverlay />
      <AuthProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </AuthProvider>
      <Toaster theme="dark" position="bottom-right" richColors closeButton />
    </div>
  );
}

export default App;
